import { Product, CasePricingInfo } from '../types';

// Custom case fee constant ($10.00)
export const CUSTOM_CASE_FEE_CENTS = 1000;

/**
 * Extract case quantities from product, handling both backend and MonoRepo formats
 */
const getCaseQuantitiesFromProduct = (product: Product): number[] => {
  return product.case_quantities || (product as any).case_quantity || [];
};

/**
 * Get the first (default) case quantity from a product
 */
export const getDefaultCaseQuantity = (product: Product): number => {
  const caseQuantities = getCaseQuantitiesFromProduct(product);
  
  if (!caseQuantities || caseQuantities.length === 0) {
    return 1;
  }
  
  // If the only value is 0, default to 1
  if (caseQuantities.length === 1 && caseQuantities[0] === 0) {
    return 1;
  }
  
  return caseQuantities[0];
};

/**
 * Check if a product has multiple case quantity options
 */
export const hasMultipleCaseOptions = (product: Product): boolean => {
  const caseQuantities = getCaseQuantitiesFromProduct(product);
  
  if (!caseQuantities || caseQuantities.length <= 1) {
    return false;
  }
  
  // If the only option is 0, it's effectively a single option
  if (caseQuantities.length === 1 && caseQuantities[0] === 0) {
    return false;
  }
  
  return true;
};

/**
 * Get pricing info for a specific case quantity
 */
export const getCasePricingInfo = (product: Product, caseQuantity: number): CasePricingInfo | null => {
  if (!product.pricing_info?.cases) {
    // If no pricing_info available (e.g., raw MonoRepo data), create fallback pricing
    const servicePrice = (product as any).service_price || 0;
    // Single quantity items don't have packing costs
    const packingCost = caseQuantity === 1 ? 0 : 200; // Default $2.00 packing cost in cents
    
    if (servicePrice > 0) {
      return {
        quantity: caseQuantity,
        total_price_cents: (servicePrice * caseQuantity) + packingCost,
        unit_price_cents: servicePrice,
        packing_cost_cents: packingCost,
        savings_vs_individual: 0
      };
    }
    
    return null;
  }
  
  return product.pricing_info.cases.find(caseInfo => caseInfo.quantity === caseQuantity) || null;
};

/**
 * Calculate total price for a specific case quantity and number of cases
 */
export const calculateTotalPrice = (product: Product, caseQuantity: number, numberOfCases: number): number => {
  const casePricing = getCasePricingInfo(product, caseQuantity);
  if (!casePricing) {
    return 0;
  }
  
  return casePricing.total_price_cents * numberOfCases;
};

/**
 * Get display text for case quantity (e.g., "50-pack", "Single")
 */
export const getCaseQuantityDisplayText = (caseQuantity: number): string => {
  if (caseQuantity === 1) {
    return 'Single';
  }
  return `${caseQuantity}-pack`;
};

/**
 * Format case pricing for display
 */
export const formatCasePrice = (priceCents: number): string => {
  return `$${(priceCents / 100).toFixed(2)}`;
};

/**
 * Get the total number of individual items in cart item
 */
export const getTotalItemCount = (caseQuantity: number, numberOfCases: number): number => {
  return caseQuantity * numberOfCases;
};

/**
 * Create a unique cart item identifier that includes case quantity
 */
export const createCartItemId = (productId: number, caseQuantity: number): string => {
  return `${productId}-${caseQuantity}`;
};

/**
 * Get maximum quantity allowed for custom cases (highest standard case - 1)
 */
export const getMaxCustomCaseQuantity = (product: Product): number => {
  const caseQuantities = getCaseQuantitiesFromProduct(product);
  
  if (!caseQuantities || caseQuantities.length === 0) {
    return 0;
  }
  
  const maxStandard = Math.max(...caseQuantities);
  
  // If max standard quantity is 1, no custom cases allowed
  if (maxStandard <= 1) {
    return 0;
  }
  
  // Otherwise, custom cases allowed up to (max - 1)
  return maxStandard - 1;
};

/**
 * Check if a product allows custom cases
 */
export const allowsCustomCases = (product: Product): boolean => {
  // Use the product's own flag if available
  if (product.allows_custom_cases !== undefined) {
    return product.allows_custom_cases;
  }
  
  // Otherwise, calculate based on case quantities
  const caseQuantities = getCaseQuantitiesFromProduct(product);
  if (!caseQuantities || caseQuantities.length === 0) {
    return false;
  }
  
  const maxQty = Math.max(...caseQuantities);
  return maxQty > 1;
};

/**
 * Check if a quantity is a valid custom case quantity for a product
 */
export const isValidCustomQuantity = (quantity: number, product: Product): boolean => {
  if (quantity <= 0) {
    return false;
  }
  
  const caseQuantities = getCaseQuantitiesFromProduct(product);
  if (!caseQuantities || caseQuantities.length === 0) {
    return false;
  }
  
  // Cannot be a standard case quantity
  if (caseQuantities.includes(quantity)) {
    return false;
  }
  
  // Must be less than the maximum standard case quantity
  const maxStandard = Math.max(...caseQuantities);
  if (quantity >= maxStandard) {
    return false;
  }
  
  return true;
};

/**
 * Check if a case quantity is a custom case (not in standard options)
 */
export const isCustomCase = (caseQuantity: number, product: Product): boolean => {
  const caseQuantities = getCaseQuantitiesFromProduct(product);
  return !caseQuantities.includes(caseQuantity);
};

/**
 * Calculate the price for a custom case
 */
export const calculateCustomCasePrice = (product: Product, customQuantity: number): number => {
  const servicePrice = product.service_price || 0;
  // Single quantity items don't have packing costs
  const packingCost = customQuantity === 1 ? 0 : (product.pricing_info?.case_packing_cost_cents || 199); // Default $1.99
  
  return (servicePrice * customQuantity) + packingCost + CUSTOM_CASE_FEE_CENTS;
};

/**
 * Get custom case display text with fee indicator
 */
export const getCustomCaseDisplayText = (quantity: number): string => {
  return `Custom ${quantity}-pack (+$10 fee)`;
}; 